import { AvailablePaymentCurrency } from "@simplyk/common";

import { AuctionBidBadge } from "./AuctionBidBadgeChip";
import { computeBidBadge } from "./bid-badge.helper";

import { isCurrentUserTopBidder } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/AuctionDetailDrawer/bid-status.helper";
import { isRateClosed } from "@/canvas/features/FormV2/helpers/useIsRateClosed";
import { getRateTileId } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/rateTileId.constant";
import { useLocaleField } from "@/canvas/features/FormV2/helpers/useLocaleField";
import { useFrontendAuctionContext } from "@/common/contexts/FrontendAuctionContext";
import { shouldDisplayAuctionRateTaxMention } from "@/common/helpers/rates";
import { useTranslate } from "@/common/hooks/useTranslate";
import { RateOutput } from "@/common/types/trpc";

interface UseAuctionRateCardParams {
  rate: RateOutput;
  formCurrency: AvailablePaymentCurrency;
  maximumPurchasableTicketsForRate: number;
  ticketingIsClosed: boolean;
  localBidAmountInCents?: number;
  autoBidMaxAmountInCents?: number;
}

interface UseAuctionRateCardResult {
  id: string;
  title: string;
  description: string;
  photoUrls: string[];
  price: number;
  formCurrency: AvailablePaymentCurrency;
  topBidderName: string | undefined;
  hasBids: boolean;
  bidBadge: AuctionBidBadge | undefined;
  userBidAmountInCents: number | undefined;
  autoBidMaxAmountInCents: number | undefined;
  isLocallyOutbid: boolean;
  isHotItem: boolean;
  isSoldOut: boolean;
  showTaxMention: boolean;
}

export function useAuctionRateCard({
  rate,
  formCurrency,
  maximumPurchasableTicketsForRate,
  ticketingIsClosed,
  localBidAmountInCents,
  autoBidMaxAmountInCents,
}: UseAuctionRateCardParams): UseAuctionRateCardResult {
  const getLocaleField = useLocaleField();
  const { t } = useTranslate();
  const { highestBidsMapByRateId, commandBidsRateIdsMap, hasAlreadyBid, bidderCommandIds } =
    useFrontendAuctionContext();

  const rateField = getLocaleField(rate.rateFields);
  const title = rateField?.title ?? "";
  const description = rateField?.description ?? "";

  const highestBidData = highestBidsMapByRateId?.[rate.id];
  const highestBidAmount = highestBidData?.amount;
  const price = highestBidAmount ?? rate.amount;

  const isHotItem = highestBidData?.isHotItem ?? false;

  const serverBid = commandBidsRateIdsMap?.[rate.id];
  const serverBidAmount = serverBid?.amount;
  const userHasServerBid = hasAlreadyBid && serverBidAmount !== undefined && serverBidAmount !== null;

  const userIsTopBidder = isCurrentUserTopBidder(highestBidData, bidderCommandIds);

  const closed = ticketingIsClosed || isRateClosed(highestBidData?.effectiveEndUtc);

  const bidBadge = computeBidBadge({
    userHasServerBid,
    closed,
    userBidStatus: serverBid?.status,
    userIsTopBidder,
  });
  const topBidderRawName = highestBidData?.topBidderName;
  const hasTopBid = highestBidAmount !== undefined && highestBidAmount !== null;
  const youLabel = t("ticketing", "auctionV2.drawer.you");

  const topBidderName = ((): string | undefined => {
    if (userIsTopBidder) {
      return topBidderRawName ? `${topBidderRawName} (${youLabel})` : youLabel;
    }
    if (hasTopBid && !topBidderRawName) {
      return t("ticketing", "auctionV2.drawer.anonymous");
    }
    return topBidderRawName ?? undefined;
  })();

  const isLocallyOutbid =
    localBidAmountInCents && highestBidAmount && !userIsTopBidder ? localBidAmountInCents <= highestBidAmount : false;

  return {
    id: getRateTileId(rate.id),
    title,
    description,
    photoUrls: rate.photoUrls ?? [],
    price,
    formCurrency,
    topBidderName,
    hasBids: hasTopBid,
    bidBadge,
    userBidAmountInCents: isLocallyOutbid ? undefined : localBidAmountInCents,
    autoBidMaxAmountInCents,
    isLocallyOutbid,
    isHotItem,
    isSoldOut: maximumPurchasableTicketsForRate <= 0,
    showTaxMention: shouldDisplayAuctionRateTaxMention(rate),
  };
}
